// "Moj račun": name, user name, role, password change, two-factor authentication, and the resets of
// this account by an administrator (with the sign-ins after each; it cannot be cleared).
import { useState } from 'react';
import type { User } from '../../../../shared/types';
import { authChanged } from '../../lib/api';
import { changePassword, getAdminResets, getMe, regenerateRecoveryCodes, totpDisable } from '../../lib/account';
import { AdminResetList, RESET_ADVICE } from '../../components/AdminResetNotice';
import {
  accountErrorText,
  CodeInput,
  codeLooksComplete,
  FormError,
  NewPasswordFields,
  newPasswordProblem,
  PasswordInput,
} from '../../components/AccountForms';
import { Modal } from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { RecoveryCodesView } from '../../components/RecoveryCodes';
import { toast } from '../../components/Toasts';
import { TotpSetupForm } from '../../components/TotpSetup';
import { LoadError, Loading, useLoaded } from './common';

export const ROLE_LABEL: Record<User['role'], string> = { admin: 'skrbnik', user: 'uporabnik' };

function ChangePasswordForm({ user, onDone }: { user: User; onDone: () => void }) {
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const problem = !current ? 'Vpišite trenutno geslo.' : newPasswordProblem(password, repeat, user.username);

  const submit = async () => {
    setTried(true);
    if (problem || busy) return;
    setBusy(true);
    setError(null);
    try {
      await changePassword({ currentPassword: current, newPassword: password });
      toast('Geslo je spremenjeno. Vse druge naprave so odjavljene.', { kind: 'success' });
      onDone();
    } catch (err) {
      setError(accountErrorText(err, 'Gesla ni bilo mogoče spremeniti.'));
      setBusy(false);
    }
  };

  return (
    <form
      className="stack subform"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <PasswordInput id="pw-current" label="Trenutno geslo" value={current} onChange={setCurrent} autoComplete="current-password" disabled={busy} />
      <NewPasswordFields
        idPrefix="pw"
        password={password}
        repeat={repeat}
        onPassword={setPassword}
        onRepeat={setRepeat}
        username={user.username}
        disabled={busy}
      />
      <p className="muted small">Po spremembi bodo vse druge naprave odjavljene (tudi aplikacija Android).</p>
      {error ? <FormError>{error}</FormError> : tried && problem ? <FormError>{problem}</FormError> : null}
      <div className="form-actions">
        <button type="button" className="btn btn--secondary" onClick={onDone} disabled={busy}>
          Prekliči
        </button>
        <button type="submit" className="btn btn--primary" disabled={busy}>
          <Icon name={busy ? 'spinner' : 'check'} size={20} /> Shrani geslo
        </button>
      </div>
    </form>
  );
}

type TotpDialog = { kind: 'enable' } | { kind: 'codes' } | { kind: 'disable' } | { kind: 'show'; codes: string[] };

/** Password + code confirmation for new recovery codes / switching 2FA off. */
function ConfirmIdentityForm({ purpose, onSubmit, onCancel }: { purpose: 'codes' | 'disable'; onSubmit: (password: string, code: string) => Promise<void>; onCancel: () => void }) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = password !== '' && codeLooksComplete(code, true);
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(password, code);
    } catch (err) {
      setError(accountErrorText(err, 'Potrditev ni uspela.'));
      setBusy(false);
    }
  };
  return (
    <form
      className="stack"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <p>
        {purpose === 'codes'
          ? 'Za nove obnovitvene kode potrdite svojo identiteto. Dosedanje kode bodo prenehale veljati.'
          : 'Za izklop dvostopenjske prijave potrdite svojo identiteto. Brez nje je račun zaščiten le z geslom.'}
      </p>
      <PasswordInput id="confirm-password" label="Geslo" value={password} onChange={setPassword} autoComplete="current-password" disabled={busy} />
      <CodeInput
        id="confirm-code"
        label="Koda iz aplikacije za preverjanje (ali obnovitvena koda)"
        value={code}
        onChange={setCode}
        allowRecovery
        disabled={busy}
      />
      {error && <FormError>{error}</FormError>}
      <div className="form-actions">
        <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={busy}>
          Prekliči
        </button>
        <button type="submit" className={`btn ${purpose === 'disable' ? 'btn--danger' : 'btn--primary'}`} disabled={!ready || busy}>
          <Icon name={busy ? 'spinner' : purpose === 'disable' ? 'close' : 'refresh'} size={20} />{' '}
          {purpose === 'codes' ? 'Ustvari nove kode' : 'Izklopi'}
        </button>
      </div>
    </form>
  );
}

/** Every reset of this account by an administrator in the last 90 days (permanent, not dismissable). */
function AdminResetHistory() {
  const history = useLoaded(getAdminResets, 'Seznama ponastavitev ni bilo mogoče naložiti.');
  return (
    <div id="ponastavitve">
      <h3 className="subheading">Ponastavitve s strani skrbnika</h3>
      <p className="muted small">
        Skrbnik, ki vam ponastavi geslo, se lahko tehnično prijavi kot vi. Zato so tukaj 90 dni zapisane vse ponastavitve vašega gesla in
        dvostopenjske prijave ter vse prijave v vaš račun po njih. Seznama ne morete izbrisati ne vi ne skrbnik.
      </p>
      {history.error && !history.data ? (
        <LoadError message={history.error} onRetry={history.reload} />
      ) : !history.data ? (
        <Loading />
      ) : history.data.resets.length === 0 ? (
        <p className="status-line status-line--ok">
          <Icon name="shield" size={20} /> V zadnjih 90 dneh skrbnik ni ponastavil vašega gesla ali dvostopenjske prijave.
        </p>
      ) : (
        <>
          <AdminResetList resets={history.data.resets} />
          <p className="small">
            <strong>{RESET_ADVICE}</strong>
          </p>
        </>
      )}
    </div>
  );
}

export function AccountSection({ user }: { user: User }) {
  const me = useLoaded(getMe, 'Podatkov o računu ni bilo mogoče naložiti.');
  const [editingPassword, setEditingPassword] = useState(false);
  const [dialog, setDialog] = useState<TotpDialog | null>(null);
  // The features' user is kept up to date (e.g. 2FA switched on in the forced setup over this page).
  const current = user;
  const require2fa = me.data?.require2fa ?? true;

  const closeDialog = () => setDialog(null);
  const afterChange = () => {
    me.reload();
    authChanged();
  };

  let dialogNode = null;
  if (dialog?.kind === 'enable') {
    dialogNode = (
      <Modal title="Vklop dvostopenjske prijave" className="modal--wide">
        <TotpSetupForm
          onEnabled={(codes) => {
            setDialog({ kind: 'show', codes });
          }}
          onCancel={closeDialog}
        />
      </Modal>
    );
  } else if (dialog?.kind === 'codes' || dialog?.kind === 'disable') {
    const purpose = dialog.kind;
    dialogNode = (
      <Modal title={purpose === 'codes' ? 'Nove obnovitvene kode' : 'Izklop dvostopenjske prijave'} onClose={closeDialog}>
        <ConfirmIdentityForm
          purpose={purpose}
          onCancel={closeDialog}
          onSubmit={async (password, code) => {
            if (purpose === 'codes') {
              const codes = await regenerateRecoveryCodes({ password, code });
              setDialog({ kind: 'show', codes });
            } else {
              await totpDisable({ password, code });
              toast('Dvostopenjska prijava je izklopljena.', { kind: 'success' });
              closeDialog();
              afterChange();
            }
          }}
        />
      </Modal>
    );
  } else if (dialog?.kind === 'show') {
    dialogNode = (
      <Modal title="Obnovitvene kode">
        <RecoveryCodesView
          codes={dialog.codes}
          username={current.username}
          doneLabel="Zapri"
          onDone={() => {
            closeDialog();
            afterChange();
          }}
        />
      </Modal>
    );
  }

  return (
    <section className="card" aria-labelledby="account-title" id="moj-racun">
      <h2 id="account-title" className="card__title">
        <Icon name="user" size={22} /> Moj račun
      </h2>
      <dl className="facts">
        <dt>Ime</dt>
        <dd>{current.displayName}</dd>
        <dt>Uporabniško ime</dt>
        <dd>{current.username}</dd>
        <dt>Vloga</dt>
        <dd>{ROLE_LABEL[current.role]}</dd>
      </dl>
      {me.error && <p className="muted small">{me.error}</p>}

      <h3 className="subheading">Geslo</h3>
      {editingPassword ? (
        <ChangePasswordForm user={current} onDone={() => setEditingPassword(false)} />
      ) : (
        <button type="button" className="btn btn--secondary" onClick={() => setEditingPassword(true)}>
          <Icon name="lock" size={20} /> Spremeni geslo
        </button>
      )}

      <h3 className="subheading">Dvostopenjska prijava</h3>
      {current.totpEnabled ? (
        <>
          <p className="status-line status-line--ok">
            <Icon name="shield" size={20} /> Vklopljena – ob prijavi potrebujete kodo iz aplikacije za preverjanje.
          </p>
          <div className="toolbar">
            <button type="button" className="btn btn--secondary" onClick={() => setDialog({ kind: 'codes' })}>
              <Icon name="refresh" size={20} /> Nove obnovitvene kode
            </button>
            {!require2fa && (
              <button type="button" className="btn btn--danger-ghost" onClick={() => setDialog({ kind: 'disable' })}>
                <Icon name="close" size={20} /> Izklopi
              </button>
            )}
          </div>
          {require2fa && <p className="muted small">Dvostopenjska prijava je obvezna za vse uporabnike, zato je ni mogoče izklopiti.</p>}
        </>
      ) : (
        <>
          <p className="status-line status-line--warning">
            <Icon name="alert" size={20} /> Ni vklopljena – račun je zaščiten le z geslom.
          </p>
          <button type="button" className="btn btn--primary" onClick={() => setDialog({ kind: 'enable' })}>
            <Icon name="shield" size={20} /> Vklopi dvostopenjsko prijavo
          </button>
        </>
      )}
      <AdminResetHistory />
      {dialogNode}
    </section>
  );
}
